import type { CartExperimentSlice } from '@/store/slices/cartExperimentSlice';

import { parseCartExperimentValue } from './index';
import type { CartExperimentState } from './types';

/** 从解析开始计时，覆盖模块加载、神策身份等待和接口请求，不依赖 HTTP 自身的超时。 */
export const CART_EXPERIMENT_TIMEOUT_MS = 5000;

export type CartExperimentConfig =
  | Readonly<{ enabled: false }>
  | Readonly<{
      enabled: true;
      /** 必须使用已确认的实验参数名，不内置猜测的线上配置。 */
      paramName: string;
      revision: string;
    }>;

export type CartExperimentStore = {
  getState: () => CartExperimentSlice & { token?: string };
};

type Resolution = {
  key: string;
  promise: Promise<CartExperimentState>;
  cancel: (state: CartExperimentState) => void;
};

export interface CartExperimentResolver {
  resolve: (
    store: CartExperimentStore,
    config: CartExperimentConfig,
  ) => Promise<CartExperimentState>;
  reset: (store: CartExperimentStore) => void;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseResponse(response: unknown, revision: string): CartExperimentState {
  if (!isRecord(response) || typeof response.code !== 'number') {
    return { status: 'fallback', reason: 'invalid' };
  }
  if (response.code !== 200) return { status: 'fallback', reason: 'unavailable' };
  const parsed = parseCartExperimentValue(
    isRecord(response.data) ? response.data.strategy_variable : undefined,
  );
  return parsed.status === 'valid'
    ? { status: 'resolved', value: parsed.value, source: 'remote', revision }
    : { status: 'fallback', reason: 'invalid' };
}

/**
 * 客户端编排核心。注入既有 API adapter，不拥有另一套 store。
 * 同一 resolver 内以 Provider Store 和认证会话隔离请求；分组仍由后端决定。
 */
export function createCartExperimentResolver(
  request: (paramName: string, signal: AbortSignal) => Promise<unknown>,
): CartExperimentResolver {
  const resolutions = new WeakMap<CartExperimentStore, Resolution>();

  const replaceWith = (
    store: CartExperimentStore,
    key: string,
    state: CartExperimentState,
  ): Promise<CartExperimentState> => {
    const previous = resolutions.get(store);
    if (previous?.key === key) return previous.promise;
    previous?.cancel(state);
    const promise = Promise.resolve(state);
    resolutions.set(store, { key, promise, cancel: () => undefined });
    store.getState().setCartExperimentState(state);
    return promise;
  };

  return {
    resolve(store, config) {
      if (!config || config.enabled !== true) {
        return replaceWith(store, 'disabled', { status: 'disabled' });
      }

      const { paramName, revision } = config;
      if (
        typeof paramName !== 'string' ||
        !paramName.trim() ||
        typeof revision !== 'string' ||
        !revision.trim()
      ) {
        return replaceWith(store, 'invalid-config', { status: 'fallback', reason: 'invalid' });
      }

      // 登录、退出或换号后不能复用上一身份的分组；同一会话的 Header / 页面仍共用请求。
      // 使用 token 而不等待账户资料，保证账户接口失败时也能解析和计价；不持久化或输出 token。
      const token = store.getState().token || '';
      const key = JSON.stringify([paramName, revision, token]);
      const previous = resolutions.get(store);
      if (previous?.key === key) return previous.promise;
      previous?.cancel({ status: 'idle' });

      let complete: (state: CartExperimentState) => void = () => undefined;
      const promise = new Promise<CartExperimentState>((resolve) => {
        complete = resolve;
      });
      let finished = false;
      const controller = new AbortController();

      const finish = (state: CartExperimentState, publish: boolean): void => {
        if (finished) return;
        // 身份已变但新消费者尚未解析时，也要结束旧等待且禁止旧分组回写。
        if (publish && (store.getState().token || '') !== token) {
          if (resolutions.get(store) === resolution) resolutions.delete(store);
          state = { status: 'idle' };
          publish = false;
        }
        finished = true;
        clearTimeout(timeout);
        controller.abort();
        // 先完成 Promise，保证同步订阅者重入 reset/resolve 时旧调用方也能结束等待。
        complete(state);
        if (publish) store.getState().setCartExperimentState(state);
      };

      const resolution: Resolution = {
        key,
        promise,
        cancel: (state) => finish(state, false),
      };
      resolutions.set(store, resolution);
      // loading 订阅者可能同步 reset/切换配置，必须先登记 timer，再发布状态。
      // 超时沿用 unavailable，由现有策略回退旧购物车 + 手动用券，不伪造真实第 1 组。
      const timeout = setTimeout(
        () => finish({ status: 'fallback', reason: 'unavailable' }, true),
        CART_EXPERIMENT_TIMEOUT_MS,
      );
      store.getState().setCartExperimentState({ status: 'loading' });

      // finish 只完成一次；超时后的迟到成功/失败不会切换页面或覆盖当前身份的策略。
      void Promise.resolve()
        .then(() => {
          if (finished) return undefined;
          return request(paramName, controller.signal);
        })
        .then((response) => (finished ? undefined : parseResponse(response, revision)))
        .then(
          (state) => {
            if (finished) return;
            if (state) finish(state, true);
          },
          () => {
            finish({ status: 'fallback', reason: 'unavailable' }, true);
          },
        );

      return promise;
    },
    reset(store) {
      const previous = resolutions.get(store);
      resolutions.delete(store);
      const state: CartExperimentState = { status: 'idle' };
      previous?.cancel(state);
      store.getState().setCartExperimentState(state);
    },
  };
}
